"use client";

import { ParallaxGroup, ParallaxLayer } from "@/components/ui/parallax";
import { motion } from "motion/react";
import Link from "next/link";
import { Fragment, type ReactNode } from "react";

interface LegalDocumentPageProps {
  title: string;
  lastUpdated: string;
  effectiveDate?: string;
  content: string;
}

type LegalBlock =
  | { type: "paragraph"; text: string }
  | { type: "unordered-list"; items: string[] }
  | { type: "ordered-list"; items: string[] }
  | { type: "subheading"; text: string };

interface LegalSection {
  title: string;
  blocks: LegalBlock[];
}

const TOP_LEVEL_HEADING = /^##\s+(.+)$/;
const SUBHEADING = /^###\s+(.+)$/;
const UNORDERED_ITEM = /^-\s+(.+)$/;
const ORDERED_ITEM = /^\d+\.\s+(.+)$/;
const INLINE_TOKEN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|`[^`]+`)/g;

function parseSections(content: string): LegalSection[] {
  const lines = content.split(/\r?\n/);
  const sections: LegalSection[] = [];
  let currentTitle: string | null = null;
  let currentLines: string[] = [];

  const flushSection = () => {
    if (!currentTitle) {
      return;
    }

    sections.push({
      title: currentTitle,
      blocks: parseBlocks(currentLines),
    });
  };

  for (const line of lines) {
    const headingMatch = line.match(TOP_LEVEL_HEADING);

    if (headingMatch) {
      flushSection();
      currentTitle = headingMatch[1].trim();
      currentLines = [];
      continue;
    }

    currentLines.push(line);
  }

  flushSection();

  return sections;
}

function parseBlocks(lines: string[]): LegalBlock[] {
  const blocks: LegalBlock[] = [];
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    const text = paragraphLines.join(" ").trim();

    if (text) {
      blocks.push({ type: "paragraph", text });
    }

    paragraphLines = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();

    if (!line) {
      flushParagraph();
      continue;
    }

    const subheadingMatch = line.match(SUBHEADING);
    if (subheadingMatch) {
      flushParagraph();
      blocks.push({ type: "subheading", text: subheadingMatch[1].trim() });
      continue;
    }

    if (UNORDERED_ITEM.test(line)) {
      flushParagraph();
      const items: string[] = [];

      while (index < lines.length) {
        const itemLine = lines[index].trim();
        const itemMatch = itemLine.match(UNORDERED_ITEM);

        if (!itemMatch) {
          index -= 1;
          break;
        }

        items.push(itemMatch[1].trim());
        index += 1;
      }

      blocks.push({ type: "unordered-list", items });
      continue;
    }

    if (ORDERED_ITEM.test(line)) {
      flushParagraph();
      const items: string[] = [];

      while (index < lines.length) {
        const itemLine = lines[index].trim();
        const itemMatch = itemLine.match(ORDERED_ITEM);

        if (!itemMatch) {
          index -= 1;
          break;
        }

        items.push(itemLine.replace(/^\d+\.\s+/, "").trim());
        index += 1;
      }

      blocks.push({ type: "ordered-list", items });
      continue;
    }

    paragraphLines.push(line);
  }

  flushParagraph();

  return blocks;
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const matches = text.match(INLINE_TOKEN);

  if (!matches) {
    return [text];
  }

  const parts = text.split(INLINE_TOKEN);

  return parts
    .filter((part) => part.length > 0)
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;

      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={key} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }

      if (part.startsWith("[") && part.includes("](") && part.endsWith(")")) {
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);

        if (!linkMatch) {
          return <Fragment key={key}>{part}</Fragment>;
        }

        const [, label, href] = linkMatch;

        if (href.startsWith("/")) {
          return (
            <Link key={key} href={href} className="text-[#ff3b30] hover:underline">
              {label}
            </Link>
          );
        }

        return (
          <a
            key={key}
            href={href}
            className="text-[#ff3b30] hover:underline"
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
          >
            {label}
          </a>
        );
      }

      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code key={key} className="rounded bg-gray-100 px-1.5 py-0.5 text-[0.95em] text-gray-800">
            {part.slice(1, -1)}
          </code>
        );
      }

      return <Fragment key={key}>{part}</Fragment>;
    });
}

function renderBlock(block: LegalBlock, sectionIndex: number, blockIndex: number) {
  const keyPrefix = `${sectionIndex}-${blockIndex}`;

  if (block.type === "subheading") {
    return (
      <h3 key={keyPrefix} className="pt-2 text-lg font-bold text-gray-900">
        {block.text}
      </h3>
    );
  }

  if (block.type === "paragraph") {
    return (
      <p key={keyPrefix} className="text-base leading-7 text-gray-600">
        {renderInline(block.text, keyPrefix)}
      </p>
    );
  }

  if (block.type === "ordered-list") {
    return (
      <ol key={keyPrefix} className="list-decimal space-y-3 pl-6 text-base leading-7 text-gray-600 marker:text-gray-500">
        {block.items.map((item, itemIndex) => (
          <li key={`${keyPrefix}-item-${itemIndex}`}>{renderInline(item, `${keyPrefix}-item-${itemIndex}`)}</li>
        ))}
      </ol>
    );
  }

  return (
    <ul key={keyPrefix} className="space-y-3">
      {block.items.map((item, itemIndex) => (
        <li key={`${keyPrefix}-item-${itemIndex}`} className="flex items-start gap-3 text-base leading-7 text-gray-600">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#ff3b30]" />
          <span>{renderInline(item, `${keyPrefix}-item-${itemIndex}`)}</span>
        </li>
      ))}
    </ul>
  );
}

export function LegalDocumentPage({
  title,
  lastUpdated,
  effectiveDate,
  content,
}: LegalDocumentPageProps) {
  const sections = parseSections(content);

  return (
    <main className="relative min-h-screen" data-section-theme="light">
      <ParallaxGroup>
        <ParallaxLayer asBackground speed={0.2} className="z-0">
          <div className="fixed inset-0 bg-gradient-to-br from-gray-50 to-white opacity-90" />
        </ParallaxLayer>

        <ParallaxLayer asBackground speed={0.4} className="pointer-events-none z-0">
          <div className="fixed inset-0">
            <div className="absolute -right-20 -top-20 h-[500px] w-[500px] rounded-full bg-[#ff3b30] opacity-5 blur-[120px]" />
            <div className="absolute -left-20 bottom-0 h-[400px] w-[400px] rounded-full bg-[#ff5e54] opacity-5 blur-[100px]" />
          </div>
        </ParallaxLayer>

        <div className="relative z-10 container mx-auto max-w-4xl px-4 py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16 text-center"
          >
            <h1 className="mb-6 text-5xl font-asgard font-bold text-gray-900 md:text-6xl">
              {title}
            </h1>
            <p className="text-xl text-gray-600">Last updated: {lastUpdated}</p>
            {effectiveDate ? (
              <p className="mt-2 text-sm text-gray-500">Effective Date: {effectiveDate}</p>
            ) : null}
          </motion.div>

          <div className="space-y-12">
            {sections.map((section, sectionIndex) => (
              <motion.section
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + sectionIndex * 0.03 }}
                className="rounded-2xl border border-gray-100 bg-white/80 p-8 shadow-sm backdrop-blur-lg"
              >
                <div className="space-y-6">
                  <div className="border-b border-gray-100 pb-4">
                    <h2 className="text-2xl font-asgard font-bold text-gray-900">
                      {section.title}
                    </h2>
                  </div>

                  <div className="space-y-5">
                    {section.blocks.map((block, blockIndex) => renderBlock(block, sectionIndex, blockIndex))}
                  </div>
                </div>
              </motion.section>
            ))}
          </div>
        </div>
      </ParallaxGroup>
    </main>
  );
}
